/* ==========================================================================
   Flight Box — adapter line-item lock.

   The Flight Box adds two cart lines: the unit and its region adapter, so the
   dropshipper's export carries both SKUs. This keeps the adapter tied to the
   unit so a customer can't end up with one and not the other:

     · adapter quantity always matches the unit quantity for the same plug
     · removing the adapter re-adds it while the unit is still there
     · removing the unit removes its adapter
     · the adapter's remove button and quantity stepper are hidden in the cart

   Enforcement is the real guarantee — the UI hiding is cosmetic, and if the
   theme's cart markup changes so the selectors miss, the quantities are still
   reconciled against the cart API.

   Loaded globally (the cart drawer opens on every page), so this lives in
   layout/theme.liquid rather than a section.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- config -----------------------------------------------------------
     Adapter variant ids, keyed by the parent's Plug Type option value. These
     must match the "Adapter variant per plug type" setting on the buy box.
     ---------------------------------------------------------------------- */
  var PARENT_HANDLE = 'flight-box';
  var ADAPTER_HANDLE = 'aircraft-vibes-flight-box-adapter';
  var ADAPTERS = {
    'us / ca': 56510122623355,
    'eu': 56510122656123,
    'uk': 56510122688891,
    'au / nz': 56510122721659
  };

  var ADAPTER_IDS = Object.keys(ADAPTERS).map(function (k) { return ADAPTERS[k]; });

  if (!window.fetch) return;

  var busy = false;

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  /* Returns, per adapter variant id, the quantity the cart should hold —
     the total units of the parent carrying that plug type. */
  function desiredAdapterQuantities(cart) {
    var want = {};
    ADAPTER_IDS.forEach(function (id) { want[id] = 0; });

    cart.items.forEach(function (item) {
      if (item.handle !== PARENT_HANDLE) return;
      // option1 is Plug Type; fall back to scanning the options array.
      var plug = item.options_with_values && item.options_with_values.length
        ? item.options_with_values[0].value
        : (item.variant_options || [])[0];
      if (!plug) return;
      var id = ADAPTERS[String(plug).trim().toLowerCase()];
      if (id) want[id] += item.quantity;
    });

    return want;
  }

  function currentAdapterQuantities(cart) {
    var have = {};
    ADAPTER_IDS.forEach(function (id) { have[id] = 0; });
    cart.items.forEach(function (item) {
      if (ADAPTER_IDS.indexOf(item.variant_id) > -1) {
        have[item.variant_id] += item.quantity;
      }
    });
    return have;
  }

  function change(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: quantity })
    });
  }

  function add(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    });
  }

  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;

    return getCart()
      .then(function (cart) {
        if (!cart || !cart.items) return false;

        var want = desiredAdapterQuantities(cart);
        var have = currentAdapterQuantities(cart);
        var jobs = [];

        ADAPTER_IDS.forEach(function (id) {
          if (want[id] === have[id]) return;
          if (have[id] === 0 && want[id] > 0) {
            // Line is gone entirely — change.js can't resurrect it, so add.
            jobs.push(add(id, want[id]));
          } else {
            // Covers both trimming to match and removing (quantity 0).
            jobs.push(change(id, want[id]));
          }
        });

        if (!jobs.length) return false;
        return Promise.all(jobs).then(function () { return true; });
      })
      .catch(function () { return false; })
      .then(function (changed) {
        busy = false;
        if (changed) {
          // Let the theme redraw with the corrected quantities.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        }
        return changed;
      });
  }

  /* ---- hide the adapter's controls in the cart ---------------------------
     Cart line markup varies by theme, so rather than guessing at class names
     we find the link pointing at the adapter product and work outwards from
     it. Anything not found is simply skipped.
     ---------------------------------------------------------------------- */
  function lockRows() {
    var links = document.querySelectorAll('a[href*="' + ADAPTER_HANDLE + '"]');
    Array.prototype.forEach.call(links, function (link) {
      var row = link.closest('tr, li, .cart-item, [class*="cart-item"], [class*="line-item"]');
      if (!row || row.hasAttribute('data-fb-locked')) return;
      row.setAttribute('data-fb-locked', 'true');

      var controls = row.querySelectorAll(
        'input[name="updates[]"], input[type="number"], ' +
        'a[href*="/cart/change"], button[name="minus"], button[name="plus"], ' +
        '[class*="quantity"], [aria-label*="emove"], [title*="emove"]'
      );
      Array.prototype.forEach.call(controls, function (el) {
        el.style.display = 'none';
      });

      // Say why it can't be changed, so an empty space isn't just confusing.
      if (!row.querySelector('[data-fb-locked-note]')) {
        var note = document.createElement('div');
        note.setAttribute('data-fb-locked-note', '');
        note.textContent = 'Included with your Flight Box';
        note.style.cssText = 'font-size:12px;font-weight:600;opacity:.65;margin-top:4px;';
        link.parentNode.appendChild(note);
      }
    });
  }

  function run() {
    reconcile().then(lockRows);
  }

  // Initial pass, plus every signal the theme gives that the cart moved.
  document.addEventListener('DOMContentLoaded', run);
  ['on:cart:add', 'on:line-item:change', 'dispatch:cart-drawer:refresh', 'dispatch:cart-drawer:open']
    .forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(run, 120); });
    });

  if (document.readyState !== 'loading') run();
})();
